/**
 * Currency registry. Symbols are restricted to glyphs that the bundled invoice fonts can
 * render without complex text shaping, which is why Gulf currencies use their ISO code.
 * Add new currencies here; everything else (validation, formatting, PDFs) picks them up.
 */
export interface CurrencyInfo {
  code: string;
  name: string;
  /** Unambiguous symbol, e.g. "CA$". */
  symbol: string;
  /** Short local symbol, e.g. "$". */
  narrowSymbol: string;
  /** Number of minor-unit digits (ISO 4217). */
  decimals: number;
  symbolPosition: 'before' | 'after';
  /** Whether a space separates the symbol from the amount. */
  symbolSpacing: boolean;
}

function currency(
  code: string,
  name: string,
  symbol: string,
  decimals = 2,
  options: { narrow?: string; position?: 'before' | 'after'; spacing?: boolean } = {},
): CurrencyInfo {
  return {
    code,
    name,
    symbol,
    narrowSymbol: options.narrow ?? symbol,
    decimals,
    symbolPosition: options.position ?? 'before',
    symbolSpacing: options.spacing ?? /^[A-Za-z]{2,}$/.test(symbol),
  };
}

export const CURRENCIES: readonly CurrencyInfo[] = [
  currency('USD', 'US Dollar', '$'),
  currency('EUR', 'Euro', '€'),
  currency('GBP', 'British Pound', '£'),
  currency('AED', 'UAE Dirham', 'AED'),
  currency('INR', 'Indian Rupee', '₹'),
  currency('SAR', 'Saudi Riyal', 'SAR'),
  currency('QAR', 'Qatari Riyal', 'QAR'),
  currency('KWD', 'Kuwaiti Dinar', 'KWD', 3),
  currency('BHD', 'Bahraini Dinar', 'BHD', 3),
  currency('OMR', 'Omani Rial', 'OMR', 3),
  currency('CAD', 'Canadian Dollar', 'CA$', 2, { narrow: '$' }),
  currency('AUD', 'Australian Dollar', 'A$', 2, { narrow: '$' }),
  currency('SGD', 'Singapore Dollar', 'S$', 2, { narrow: '$' }),
  currency('JPY', 'Japanese Yen', '¥', 0),
  currency('CNY', 'Chinese Yuan', 'CN¥', 2, { narrow: '¥' }),
  currency('CHF', 'Swiss Franc', 'CHF'),
  currency('NZD', 'New Zealand Dollar', 'NZ$', 2, { narrow: '$' }),
  currency('HKD', 'Hong Kong Dollar', 'HK$', 2, { narrow: '$' }),
  currency('SEK', 'Swedish Krona', 'kr', 2, { position: 'after', spacing: true }),
  currency('NOK', 'Norwegian Krone', 'kr', 2, { position: 'after', spacing: true }),
  currency('DKK', 'Danish Krone', 'kr.', 2, { position: 'after', spacing: true }),
  currency('PLN', 'Polish Złoty', 'zł', 2, { position: 'after', spacing: true }),
  currency('CZK', 'Czech Koruna', 'Kč', 2, { position: 'after', spacing: true }),
  currency('HUF', 'Hungarian Forint', 'Ft', 2, { position: 'after', spacing: true }),
  currency('ZAR', 'South African Rand', 'R', 2, { spacing: true }),
  currency('MXN', 'Mexican Peso', 'MX$', 2, { narrow: '$' }),
  currency('BRL', 'Brazilian Real', 'R$', 2, { spacing: true }),
  currency('KRW', 'South Korean Won', '₩', 0),
  currency('TRY', 'Turkish Lira', '₺'),
  currency('ILS', 'Israeli New Shekel', '₪'),
  currency('EGP', 'Egyptian Pound', 'E£'),
  currency('JOD', 'Jordanian Dinar', 'JOD', 3),
  currency('PKR', 'Pakistani Rupee', 'Rs', 2, { spacing: true }),
  currency('LKR', 'Sri Lankan Rupee', 'Rs', 2, { spacing: true }),
  currency('BDT', 'Bangladeshi Taka', 'Tk', 2, { spacing: true }),
  currency('NGN', 'Nigerian Naira', '₦'),
  currency('KES', 'Kenyan Shilling', 'KSh', 2, { spacing: true }),
  currency('MYR', 'Malaysian Ringgit', 'RM', 2, { spacing: false }),
  currency('THB', 'Thai Baht', 'THB'),
  currency('IDR', 'Indonesian Rupiah', 'Rp', 0, { spacing: true }),
  currency('PHP', 'Philippine Peso', '₱'),
  currency('VND', 'Vietnamese Dong', '₫', 0, { position: 'after', spacing: true }),
];

const CURRENCY_MAP = new Map(CURRENCIES.map((c) => [c.code, c]));

export const CURRENCY_CODES: readonly string[] = CURRENCIES.map((c) => c.code);

export function isSupportedCurrency(code: string): boolean {
  return CURRENCY_MAP.has(code);
}

export function getCurrency(code: string): CurrencyInfo {
  const info = CURRENCY_MAP.get(code.toUpperCase());
  if (info) return info;
  return currency(code.toUpperCase(), code.toUpperCase(), code.toUpperCase());
}

export function currencyDecimals(code: string): number {
  return getCurrency(code).decimals;
}

export type CurrencyDisplay = 'symbol' | 'narrow' | 'code';
